import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Mail,
  RefreshCcw,
  Send,
  Eye,
  MousePointerClick,
  CheckCircle2,
  Clock,
  Search,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import SortableTable from '../../components/SortableTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  activated: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  clicked: 'bg-sky-100 text-sky-700 border-sky-200',
  opened: 'bg-amber-100 text-amber-700 border-amber-200',
  sent: 'bg-slate-100 text-slate-600 border-slate-200',
  not_sent: 'bg-rose-50 text-rose-600 border-rose-200',
};

const STATUS_LABEL = {
  activated: 'Activated',
  clicked: 'Clicked',
  opened: 'Opened',
  sent: 'Sent',
  not_sent: 'Not Sent',
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
};

const FunnelTile = ({ label, value, suffix, icon: Icon, tone = 'navy', testId }) => {
  const tones = {
    navy: 'bg-[#002249]/5 border-[#002249]/20 text-[#002249]',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div className={`u-card p-4 rounded-xl border ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold leading-none">
        {value}
        {suffix && <span className="text-[14px] font-semibold opacity-70 ml-1">{suffix}</span>}
      </p>
    </div>
  );
};

const AdminInvitationsPage = () => {
  const [invites, setInvites] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [resendingId, setResendingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [listRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/invitations`, { withCredentials: true }),
        axios.get(`${API}/admin/communications/analytics`, { withCredentials: true }),
      ]);
      setInvites(listRes.data || []);
      setAnalytics(analyticsRes.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load invitation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleResend = async (user) => {
    if (!window.confirm(`Resend the DCCF activation email to ${user.email}?`)) return;
    setResendingId(user.id);
    try {
      await axios.post(`${API}/admin/invitations/${user.id}/resend`, {}, { withCredentials: true });
      toast.success(`Invitation resent to ${user.email}`);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const filtered = useMemo(() => {
    return (invites || []).filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (u.name || '').toLowerCase().includes(s) ||
          (u.email || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [invites, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: invites.length, sent: 0, opened: 0, clicked: 0, activated: 0, not_sent: 0 };
    invites.forEach((u) => { counts[u.status] = (counts[u.status] || 0) + 1; });
    return counts;
  }, [invites]);

  const columns = [
    {
      id: 'name',
      label: 'Member',
      accessor: (u) => u.name || '',
      render: (u) => (
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-slate-900 truncate">{u.name || '—'}</p>
          <p className="text-[11.5px] text-slate-500 truncate">{u.email}</p>
        </div>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (u) => u.status,
      render: (u) => (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide border ${STATUS_BADGE[u.status] || 'bg-slate-100 text-slate-600'}`}>
          {STATUS_LABEL[u.status] || u.status}
        </span>
      ),
    },
    {
      id: 'sent_at',
      label: 'Sent',
      accessor: (u) => u.sent_at || '',
      render: (u) => <span className="text-[12px] text-slate-600">{formatRelative(u.sent_at)}</span>,
    },
    {
      id: 'opens',
      label: 'Opens',
      accessor: (u) => u.opens || 0,
      render: (u) => (
        <span className="text-[12px] font-semibold text-slate-700">{u.opens || 0}</span>
      ),
    },
    {
      id: 'clicks',
      label: 'Clicks',
      accessor: (u) => u.clicks || 0,
      render: (u) => (
        <span className="text-[12px] font-semibold text-slate-700">{u.clicks || 0}</span>
      ),
    },
    {
      id: 'last_activity',
      label: 'Last Activity',
      accessor: (u) => u.last_clicked_at || u.last_opened_at || '',
      render: (u) => (
        <span className="text-[12px] text-slate-600">
          {formatRelative(u.last_clicked_at || u.last_opened_at)}
        </span>
      ),
    },
    {
      id: 'resend_count',
      label: 'Resends',
      accessor: (u) => u.resend_count || 0,
      render: (u) => (
        <span className="text-[12px] text-slate-600">{u.resend_count || 0}</span>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      render: (u) => (
        <button
          onClick={() => handleResend(u)}
          disabled={resendingId === u.id || u.status === 'activated'}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#002249] bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed px-3 h-8 rounded-md"
          data-testid={`resend-invite-${u.id}`}
        >
          <Send className={`w-3.5 h-3.5 ${resendingId === u.id ? 'animate-pulse' : ''}`} />
          {u.status === 'activated' ? 'Activated' : (resendingId === u.id ? 'Sending…' : 'Resend')}
        </button>
      ),
    },
  ];

  const funnel = analytics?.invites || {};
  const newsletters = analytics?.newsletters || {};

  return (
    <div className="p-6 lg:p-8" data-testid="admin-invitations-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#002249] flex items-center gap-2">
            <Mail className="w-6 h-6" /> Invitations & Communications
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Track every activation email — sent, opened, clicked, activated — plus newsletter delivery health.
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="inline-flex items-center gap-2 text-[13px] font-medium text-[#002249] bg-white border border-slate-200 hover:bg-slate-50 px-3.5 py-2 rounded-lg shadow-sm"
          data-testid="invitations-refresh-btn"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Funnel analytics */}
      <div className="mb-6">
        <h2 className="text-[13px] font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Invitation Funnel
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <FunnelTile label="Total" value={funnel.total || 0} icon={Mail} tone="navy" testId="funnel-total" />
          <FunnelTile label="Sent" value={funnel.sent || 0} icon={Send} tone="navy" testId="funnel-sent" />
          <FunnelTile label="Opened" value={funnel.opened || 0} icon={Eye} tone="amber" testId="funnel-opened" />
          <FunnelTile label="Clicked" value={funnel.clicked || 0} icon={MousePointerClick} tone="sky" testId="funnel-clicked" />
          <FunnelTile label="Activated" value={funnel.activated || 0} icon={CheckCircle2} tone="emerald" testId="funnel-activated" />
          <FunnelTile label="Pending" value={funnel.pending || 0} icon={Clock} tone="amber" testId="funnel-pending" />
          <FunnelTile
            label="Open Rate"
            value={funnel.open_rate || 0}
            suffix="%"
            icon={TrendingUp}
            tone="emerald"
            testId="funnel-open-rate"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <FunnelTile
            label="Click Rate"
            value={funnel.click_rate || 0}
            suffix="%"
            icon={TrendingUp}
            tone="sky"
            testId="funnel-click-rate"
          />
          <FunnelTile
            label="Activation Rate"
            value={funnel.activation_rate || 0}
            suffix="%"
            icon={TrendingUp}
            tone="emerald"
            testId="funnel-activation-rate"
          />
          <FunnelTile
            label="Newsletter Campaigns"
            value={newsletters.campaigns_sent || 0}
            icon={Mail}
            tone="navy"
            testId="newsletter-campaigns"
          />
          <FunnelTile
            label="Newsletter Delivered"
            value={newsletters.emails_delivered || 0}
            icon={CheckCircle2}
            tone="emerald"
            testId="newsletter-delivered"
          />
        </div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-slate-200 p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by member name or email"
            className="w-full pl-9 pr-3 h-10 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
            data-testid="invitations-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap overflow-x-auto scrollbar-hide">
          {[
            { value: 'all', label: 'All' },
            { value: 'not_sent', label: 'Not Sent' },
            { value: 'sent', label: 'Sent' },
            { value: 'opened', label: 'Opened' },
            { value: 'clicked', label: 'Clicked' },
            { value: 'activated', label: 'Activated' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 h-9 rounded-full text-[12px] font-semibold border transition-all whitespace-nowrap ${
                statusFilter === opt.value
                  ? 'bg-[#002249] text-white border-[#002249]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#002249]/40'
              }`}
              data-testid={`invitations-filter-${opt.value}`}
            >
              {opt.label}
              <span className="ml-1.5 text-[10.5px] opacity-70">{statusCounts[opt.value] ?? 0}</span>
            </button>
          ))}
        </div>
      </motion.div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <SortableTable
          columns={columns}
          data={filtered}
          defaultSort={{ id: 'sent_at', direction: 'desc' }}
          rowTestId={(u, i) => `invitation-row-${i}`}
          emptyMessage={loading ? 'Loading invitations…' : 'No invitations match your filters.'}
          stickyHeader
        />
      </div>
    </div>
  );
};

export default AdminInvitationsPage;
